import type { IpnsString } from '@wovin/core/applog'
import type { IPNSPublishTarget, SignedIPNSRecord } from '@wovin/core/ipns'
import type { CoPublishSettleState, IShare } from '@wovin/core/pubsub'
import type { WriteableThread } from '@wovin/core/thread'

import { dateNowIso } from '@wovin/core'
import { toIpnsString, tryParseCID } from '@wovin/core/ipfs'
import { createSignedIPNSRecord, unmarshalIPNSRecord } from '@wovin/core/ipns'
import { Logger } from 'besonders-logger'
import { base64pad } from 'multiformats/bases/base64'
import { CID } from 'multiformats/cid'
import { useAgent } from '../data/agent/AgentState'
import { insertCompleteApplogsInAppDBifMissing } from '../data/ApplogDB'
import { notifyToast } from '../ui/utils-ui'
import { resolveIPNS, retrievePubToThread } from './retrievePubToThread'
import { storageState } from './storage'
import { pushShare } from './store-sync'
import { parseStoredCID } from './cid-utils'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO)

// =============================================================================
// Constants
// =============================================================================

/**
 * Ritual settle window: how long to wait after a push before checking whether
 * our record is still the live IPNS head. Only used on the trustless / pure-DHT
 * path; the CAS path is zero-wait.
 */
export const CO_PUBLISH_SETTLE_MS = 60_000

/**
 * Max pull→merge→republish retries on the ritual path before giving up. Bounded
 * retries are safe because `runConvergenceCheck` guarantees eventual repair on a
 * later sync tick — exhaustion is delayed convergence, never data loss.
 */
export const CO_PUBLISH_MAX_RETRIES = 3

/** Backoff factor applied to the settle window after each ritual retry. */
export const CO_PUBLISH_BACKOFF = 1.5

/**
 * Stale-CID guard slack (ms). On the ritual path, IPNS propagation latency can
 * make `resolveIPNS` briefly return an older CID even with no real conflict. We
 * only treat a differing live CID as a conflict if the live manifest's
 * `publishTs` is within `[pushTs - slack, now]` AND the publisher is another
 * agent.
 */
const STALE_CID_SLACK_MS = 5_000

// =============================================================================
// In-flight / pending settle tracking
// =============================================================================

/** One in-flight settle per share id — prevents overlapping ritual checks. */
const inFlightSettles = new Set<string>()

/**
 * Pending ritual timers, keyed by share id. `runPendingSettles()` walks this
 * map and fires elapsed timers — a backstop for backgrounded tabs where
 * `setTimeout` is throttled. The `setTimeout` armed in `pushCoShareRitual` is
 * the primary trigger; this map lets the sync tick re-fire if the timer was
 * starved.
 */
interface PendingSettle {
	shareId: string
	fireAt: number // Date.now() ms when the settle check should run
	pushCid: string
	pushTs: string
	retries: number
	settleMs: number
	thread: WriteableThread
}
const pendingSettles = new Map<string, PendingSettle>()

// =============================================================================
// Helpers
// =============================================================================

/**
 * Convert a share id (k51... string) to the tagged `IpnsString`. Returns null
 * if the id is missing or not a valid IPNS cid.
 */
function shareIdToIpns(share: IShare): IpnsString | null {
	if (!share.id) return null
	const parsed = tryParseCID(share.id)
	if (!parsed?.cid || !parsed.isIpns) return null
	return toIpnsString(parsed.cid)
}

/**
 * Build the main-thread IPNS publish targets from configured ucan-store-proxy
 * connectors. Mirrors what the sync worker assembles in `pushIOInWorker`, but
 * only the proxy connectors (the CAS-capable path) — kubo is worker-side.
 */
function getIPNSPublishTargets(): IPNSPublishTarget[] {
	const targets: IPNSPublishTarget[] = []
	for (const connector of storageState.ucanUploads ?? []) {
		targets.push(connector.asIPNSTarget())
	}
	return targets
}

/**
 * Whether any configured publish target can resolve the current IPNS sequence
 * (the CAS precondition). If yes the share can settle CAS-first (zero-wait); if
 * no it falls back to the 1-minute ritual.
 */
export function hasCASResolveCapability(): boolean {
	return getIPNSPublishTargets().some(t => typeof t.resolveSequence === 'function')
}

/**
 * Pick the settle mode for a co-share on its first push. Probes the configured
 * targets for a `resolveSequence`-capable one.
 */
export function probeSettleMode(): 'cas' | 'ritual' {
	return hasCASResolveCapability() ? 'cas' : 'ritual'
}

/**
 * Extract `{ publisherAg, publishTs }` from a snapshot's info logs. The
 * originator stamps a `share/publishTs` applog (`ag` = publishing agent, `vl` =
 * ISO ms timestamp) into every push (see `prepareSnapshotForPush`). Returns null
 * if no such log is present (e.g. a legacy / pre-co-publish snapshot).
 */
function extractPublishManifest(
	infoLogs: { at?: string; ag?: string; vl?: unknown }[] | null | undefined,
): { publisherAg: string; publishTs: string } | null {
	if (!infoLogs?.length) return null
	const tsLog = infoLogs.find(log => log?.at === 'share/publishTs')
	if (!tsLog || !tsLog.ag || typeof tsLog.vl !== 'string') return null
	return { publisherAg: tsLog.ag, publishTs: tsLog.vl }
}

/**
 * Fetch the raw marshalled IPNS record bytes + sequence from a naming service.
 * Used by the equal-sequence tie-break to compare `recordBytes` lexicographically.
 * Returns null if the name was never published or the service only returned a
 * value (no raw record to compare).
 */
async function resolveIPNSRecordBytes(
	nameServiceUrl: string,
	ipnsName: string,
): Promise<{ recordBytes: Uint8Array; sequence: bigint } | null> {
	const url = `${nameServiceUrl}/name/${ipnsName}`
	let response: Response
	try {
		response = await fetch(url)
	} catch (err) {
		throw new Error(`Network error resolving IPNS record ${ipnsName}: ${err}`)
	}
	if (response.status === 404) return null
	if (!response.ok) {
		throw new Error(`HTTP ${response.status} resolving IPNS record ${ipnsName}: ${response.statusText}`)
	}
	const { record } = await response.json()
	if (!record) return null
	const bytes = base64pad.baseDecode(record)
	const entry = unmarshalIPNSRecord(bytes)
	return { recordBytes: bytes, sequence: entry.sequence }
}

// =============================================================================
// Tie-break (equal-sequence, different publisher)
// =============================================================================

/**
 * Lexicographic, unsigned byte-by-byte comparison of two `Uint8Array`s.
 * Returns <0 if `a` < `b`, 0 if equal, >0 if `a` > `b`.
 */
export function compareRecordBytes(a: Uint8Array, b: Uint8Array): number {
	const len = Math.min(a.length, b.length)
	for (let i = 0; i < len; i++) {
		const diff = a[i]! - b[i]!
		if (diff !== 0) return diff
	}
	return a.length - b.length
}

/**
 * Determine the loser of an equal-sequence tie-break. The agent whose
 * `SignedIPNSRecord.recordBytes` is lexicographically **lesser** is the loser
 * (manu's memo rule) and must pull/integrate the winner and republish with a
 * bumped sequence. Returns `'me'` if `myRecord` is the loser, `'them'` otherwise.
 */
export function tieBreakLoser(myRecord: SignedIPNSRecord, liveRecord: SignedIPNSRecord): 'me' | 'them' {
	return compareRecordBytes(myRecord.recordBytes, liveRecord.recordBytes) < 0 ? 'me' : 'them'
}

// =============================================================================
// CAS path — zero-wait
// =============================================================================

/**
 * CAS settle for a co-share. The actual IPNS publish (with `publishIPNSRecord`'s
 * sequence CAS + self-heal on "existing >= new" conflict) already happened
 * inside the sync worker's `pushIOInWorker` during `pushShare`. This function
 * resolves the sequence the record landed at and records the settle state as
 * `won` — zero-wait, no 60s sleep. If the share was still probing its settle
 * mode, it is pinned to `'cas'`.
 *
 * `cid` is the snapshot CID that was just pushed.
 */
export async function pushCoShareCAS(share: IShare, cid: string): Promise<void> {
	const agent = useAgent()
	const ipns = shareIdToIpns(share)
	if (!ipns) {
		WARN('[co-publish/CAS] share has no resolvable ipns id; skipping settle', share.id)
		return
	}

	// Resolve the sequence our record landed at. The worker's publishIPNSRecord
	// already self-healed any conflict, so the live seq is our final seq. We try
	// each CAS-capable target; the first that answers wins.
	let seq: bigint | null = null
	for (const target of getIPNSPublishTargets()) {
		if (typeof target.resolveSequence !== 'function') continue
		try {
			seq = await target.resolveSequence(ipns)
			break
		} catch (err) {
			DEBUG(`[co-publish/CAS] target '${target.name}' resolveSequence failed`, err)
		}
	}

	const lastSettle: CoPublishSettleState = {
		pushTs: dateNowIso(),
		pushCid: cid,
		seq: seq ?? undefined,
		status: 'won',
		retries: 0,
		mode: 'cas',
	}
	await agent.updateShare(share.id, { settleMode: 'cas', lastSettle })
	LOG('[co-publish/CAS] settle won', { shareId: share.id, cid, seq: seq?.toString() })
}

// =============================================================================
// Ritual path — fixed 1-min, trustless / pure-DHT fallback
// =============================================================================

/**
 * Ritual settle for a co-share. After `pushShare` has pushed the snapshot
 * (which already published the IPNS record), schedule a settle check after
 * `CO_PUBLISH_SETTLE_MS` (or `share.coSettleMs`): re-resolve IPNS and, if
 * another co-publisher's record is now live, pull → integrate → republish with
 * backoff up to `CO_PUBLISH_MAX_RETRIES`. Non-blocking — the caller does not
 * wait. One in-flight settle per share id.
 */
export function pushCoShareRitual(share: IShare, thread: WriteableThread, pushCid: string, pushTs: string): void {
	if (!share.id) return
	const settleMs = share.coSettleMs ?? CO_PUBLISH_SETTLE_MS

	// Record pending state immediately.
	const agent = useAgent()
	const lastSettle: CoPublishSettleState = {
		pushTs,
		pushCid,
		status: 'pending',
		retries: 0,
		mode: 'ritual',
	}
	void agent.updateShare(share.id, { settleMode: 'ritual', lastSettle })

	armRitualSettle(share.id, thread, pushCid, pushTs, 0, settleMs)
}

/** Arm (or re-arm after a retry) a ritual settle check. Stores the pending entry for `runPendingSettles` and schedules the setTimeout. */
function armRitualSettle(
	shareId: string,
	thread: WriteableThread,
	pushCid: string,
	pushTs: string,
	retries: number,
	settleMs: number,
): void {
	const fireAt = Date.now() + settleMs
	pendingSettles.set(shareId, { shareId, fireAt, pushCid, pushTs, retries, settleMs, thread })
	DEBUG('[co-publish/ritual] armed settle', { shareId, pushCid, retries, settleMs, fireAt })

	setTimeout(() => void runSettleCheck(shareId), settleMs)
}

/** The actual settle check, run after the settle window elapses. Guarded by the in-flight Set so only one check per share runs at a time. */
async function runSettleCheck(shareId: string): Promise<void> {
	const pending = pendingSettles.get(shareId)
	if (!pending) return // cleared (e.g. share deleted / converged) — nothing to do
	if (inFlightSettles.has(shareId)) {
		DEBUG('[co-publish/ritual] settle already in-flight', shareId)
		return
	}
	inFlightSettles.add(shareId)
	try {
		const agent = useAgent()
		const share = agent.sharesMap.get(shareId)
		if (!share || share.isDeleted) {
			pendingSettles.delete(shareId)
			return
		}
		const ipns = shareIdToIpns(share)
		if (!ipns) {
			pendingSettles.delete(shareId)
			return
		}

		// Re-resolve the live IPNS head.
		let liveCid: CID | null
		try {
			liveCid = await resolveIPNS(ipns)
		} catch (err) {
			WARN('[co-publish/ritual] resolveIPNS failed; will retry next tick', { shareId, err })
			return
		}
		const pushCid = pending.pushCid
		const liveCidStr = liveCid ? liveCid.toV1().toString() : null
		DEBUG('[co-publish/ritual] settle check', { shareId, pushCid, liveCidStr })

		// My push is still the live record → won.
		if (liveCidStr && liveCidStr === CID.parse(pushCid).toV1().toString()) {
			await agent.updateShare(shareId, {
				lastSettle: { ...share.lastSettle, pushTs: pending.pushTs, pushCid, status: 'won', retries: pending.retries, mode: 'ritual' },
			})
			pendingSettles.delete(shareId)
			LOG('[co-publish/ritual] settle won', { shareId, pushCid })
			return
		}
		if (!liveCidStr) {
			// Could not resolve a live CID — treat as transient, retry next tick.
			WARN('[co-publish/ritual] no live CID resolved; will retry next tick', shareId)
			return
		}

		// A different CID is live. Fetch its manifest to apply the stale-CID guard.
		let pulled: Awaited<ReturnType<typeof retrievePubToThread>> | null = null
		try {
			pulled = await retrievePubToThread(pending.thread, ipns, { pinCID: liveCidStr, subID: shareId }, pending.thread)
		} catch (err) {
			WARN('[co-publish/ritual] retrievePubToThread failed during settle; will retry next tick', { shareId, err })
			return
		}

		const manifest = extractPublishManifest(pulled.info?.logs)
		const myAg = agent.ag
		const now = Date.now()
		const pushTsMs = Date.parse(pending.pushTs)

		// Stale-CID guard: only treat as a conflict if the live manifest's
		// publishTs is within [pushTs - slack, now] AND the publisher is another
		// agent. If the live publisher is us, it's propagation lag of our own
		// record → won. If the live publishTs is older than our push, our push is
		// actually newer → won.
		if (manifest) {
			const livePublisherAg = manifest.publisherAg
			const livePublishTsMs = Date.parse(manifest.publishTs)
			if (livePublisherAg === myAg) {
				await agent.updateShare(shareId, {
					lastSettle: { ...share.lastSettle, pushTs: pending.pushTs, pushCid, status: 'won', retries: pending.retries, mode: 'ritual' },
				})
				pendingSettles.delete(shareId)
				DEBUG('[co-publish/ritual] live record is our own; won', { shareId, livePublisherAg })
				return
			}
			if (!Number.isNaN(livePublishTsMs) && livePublishTsMs < pushTsMs - STALE_CID_SLACK_MS) {
				await agent.updateShare(shareId, {
					lastSettle: { ...share.lastSettle, pushTs: pending.pushTs, pushCid, status: 'won', retries: pending.retries, mode: 'ritual' },
				})
				pendingSettles.delete(shareId)
				DEBUG('[co-publish/ritual] live record older than our push; won', {
					shareId,
					livePublishTs: manifest.publishTs,
					pushTs: pending.pushTs,
				})
				return
			}
			// publishTs outside [pushTs - slack, now] → indeterminate, retry next tick
			if (!Number.isNaN(livePublishTsMs) && livePublishTsMs > now) {
				WARN('[co-publish/ritual] live publishTs in the future; deferring', { shareId, livePublishTs: manifest.publishTs })
				return
			}
		}

		// Equal-sequence tie-break: if the live record's seq equals the seq we
		// published at and the publisher is another agent, the loser (lesser
		// recordBytes) pulls/integrates/republishes with a bumped seq. If we are
		// the winner, we keep our record and the other agent will merge us.
		const mySeq = share.lastSettle?.seq
		if (manifest && mySeq !== undefined) {
			const targets = getIPNSPublishTargets()
			const casTarget = targets.find(t => typeof t.resolveSequence === 'function')
			if (casTarget) {
				try {
					const nameServiceUrl = casTarget.name.replace(/^ucan-proxy:/, '')
					const live = await resolveIPNSRecordBytes(nameServiceUrl, ipns)
					if (live && live.sequence === mySeq) {
						const myRecord = await createSignedIPNSRecord(share.pk, CID.parse(pushCid), mySeq)
						const loser = tieBreakLoser(
							myRecord,
							{ recordBytes: live.recordBytes, sequence: live.sequence, ipnsName: ipns, value: '' } as SignedIPNSRecord,
						)
						if (loser === 'them') {
							// We won the tie-break — no action needed.
							await agent.updateShare(shareId, {
								lastSettle: {
									...share.lastSettle,
									pushTs: pending.pushTs,
									pushCid,
									status: 'won',
									retries: pending.retries,
									mode: 'ritual',
								},
							})
							pendingSettles.delete(shareId)
							LOG('[co-publish/ritual] won equal-seq tie-break', { shareId, mySeq: mySeq.toString() })
							return
						}
						DEBUG('[co-publish/ritual] lost equal-seq tie-break; pulling/integrating/republishing', { shareId, mySeq: mySeq.toString() })
					}
				} catch (err) {
					// Tie-break is a refinement — fall through to the normal
					// lost-handling if record bytes can't be fetched.
					DEBUG('[co-publish/ritual] tie-break probe failed; falling back to lost', { shareId, err })
				}
			}
		}

		// We lost the minute — integrate the live snapshot and republish a merged one.
		// `retrievePubToThread` already integrated the content applogs; also insert
		// the info logs (matching `retrievePubAsThread`).
		insertCompleteApplogsInAppDBifMissing(pulled.info?.logs ?? [])

		let reCid: string | null = null
		try {
			const result = await pushShare(pending.thread, share)
			// pushShare returns the shareNameString on success and updates
			// share.lastCID; read the new CID from the refreshed share.
			const refreshed = agent.sharesMap.get(shareId)
			reCid = refreshed?.lastCID ?? null
			VERBOSE('[co-publish/ritual] republished merged snapshot', { shareId, reCid, result })
		} catch (err) {
			ERROR('[co-publish/ritual] republish failed during settle', { shareId, err })
		}

		const nextRetries = pending.retries + 1
		const nextSettleMs = Math.round(pending.settleMs * CO_PUBLISH_BACKOFF)

		if (reCid && nextRetries <= CO_PUBLISH_MAX_RETRIES) {
			// Merged — re-arm with the new pushCid so we verify the merged record wins.
			await agent.updateShare(shareId, {
				lastSettle: { pushTs: dateNowIso(), pushCid: reCid, status: 'merged', retries: nextRetries, mode: 'ritual' },
			})
			armRitualSettle(shareId, pending.thread, reCid, dateNowIso(), nextRetries, nextSettleMs)
			LOG('[co-publish/ritual] merged & re-armed', { shareId, reCid, retries: nextRetries, nextSettleMs })
		} else {
			// Exhausted retries (or republish failed) — give up this attempt. The
			// convergence check on the next sync tick will re-attempt, so this is
			// delayed convergence, not data loss.
			await agent.updateShare(shareId, {
				lastSettle: { pushTs: pending.pushTs, pushCid, status: 'failed', retries: nextRetries, mode: 'ritual' },
			})
			pendingSettles.delete(shareId)
			notifyToast(`Co-publish settle gave up after ${nextRetries} retries for "${share.name}" — will retry on next sync`, 'warning')
			WARN('[co-publish/ritual] settle failed (exhausted retries)', { shareId, retries: nextRetries })
		}
	} finally {
		inFlightSettles.delete(shareId)
	}
}

// =============================================================================
// Pending-settle backstop (backgrounded tabs)
// =============================================================================

/**
 * Fire any elapsed ritual settle timers. Called each sync tick as a backstop for
 * backgrounded tabs where `setTimeout` is throttled and may not fire on time.
 * The armed `setTimeout` is the primary trigger; this re-fires elapsed entries
 * that the timer missed.
 */
export function runPendingSettles(): void {
	const now = Date.now()
	for (const [shareId, pending] of pendingSettles) {
		if (pending.fireAt <= now && !inFlightSettles.has(shareId)) {
			DEBUG('[co-publish/ritual] backstop firing elapsed settle', { shareId, fireAt: pending.fireAt, now })
			void runSettleCheck(shareId)
		}
	}
}

// =============================================================================
// Convergence check — eventual-repair guarantee (both paths)
// =============================================================================

/**
 * Each sync tick, for a co-publish share: resolve the IPNS head and check
 * whether our last push is still the live record. If it isn't (another
 * co-publisher won, or our ritual settle exhausted retries), pull + integrate
 * the live snapshot and republish a merged one. This makes bounded retries
 * safe: worst case is *delayed* convergence, never *loss*.
 *
 * `thread` is the app thread (the same `Thread` passed to `pushShare`).
 */
export async function runConvergenceCheck(share: IShare, thread: WriteableThread): Promise<void> {
	if (!share.coPublish) return
	if (!share.id) return
	const ipns = shareIdToIpns(share)
	if (!ipns) return

	const agent = useAgent()

	let liveCid: CID | null
	try {
		liveCid = await resolveIPNS(ipns)
	} catch (err) {
		DEBUG('[co-publish/convergence] resolveIPNS failed; skipping this tick', { shareId: share.id, err })
		return
	}
	const liveCidStr = liveCid ? liveCid.toV1().toString() : null
	const myLastCid = share.lastCID ? parseStoredCID(share.lastCID, 'co-publish settle lastCID').toV1().toString() : null

	// My last push is still the live head → converged.
	if (liveCidStr && myLastCid && liveCidStr === myLastCid) {
		const st = share.lastSettle
		if (st && st.status === 'pending') {
			await agent.updateShare(share.id, { lastSettle: { ...st, status: 'won' } })
		}
		return
	}
	if (!liveCidStr) {
		// Nothing published yet under this name — nothing to converge toward.
		return
	}

	DEBUG('[co-publish/convergence] head differs from last push; pulling + merging + republishing', {
		shareId: share.id,
		myLastCid,
		liveCidStr,
	})

	try {
		// Pull + integrate the live snapshot. `retrievePubToThread` integrates
		// content applogs (incl. encrypted via tryIntegrateApplogs); insert the
		// info logs too (matching `retrievePubAsThread`).
		const pulled = await retrievePubToThread(thread, ipns, { pinCID: liveCidStr, subID: share.id }, thread)
		insertCompleteApplogsInAppDBifMissing(pulled.info?.logs ?? [])

		// Republish a merged snapshot that includes both our local applogs and
		// the newly integrated ones. If we have nothing new, `pushShare` is a
		// no-op (early return on no new logs) — convergence achieved by the pull.
		await pushShare(thread, share)

		const st = share.lastSettle
		const fallback: CoPublishSettleState = {
			pushTs: dateNowIso(),
			pushCid: liveCidStr,
			status: 'merged',
			retries: 0,
			mode: share.settleMode === 'cas' ? 'cas' : 'ritual',
		}
		await agent.updateShare(share.id, { lastSettle: { ...(st ?? fallback), status: 'merged' } })
		LOG('[co-publish/convergence] converged', { shareId: share.id, liveCidStr })
	} catch (err) {
		ERROR('[co-publish/convergence] failed this tick', { shareId: share.id, err })
	}
}
